import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError, NetworkError } from '../lib/api';
import type { Tenant, TenantsResponse } from '../types';
import { formatDate } from '../lib/format';
import { StatusBadge } from '../components/StatusBadge';
import { StateBlock } from '../components/StateBlock';
import { TableSkeleton } from '../components/TableSkeleton';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; tenants: Tenant[] };

/**
 * Estado de la acción suspender/reactivar de UNA fila. Se trackea por
 * tenant id en un mapa: un fallo en un tenant no afecta a los demás ni
 * desmonta la lista (eso lo gobierna LoadState, solo para la carga inicial).
 *  - 'idle'          : sin acción en curso.
 *  - 'prompting'     : mostrando el campo de motivo antes de suspender.
 *  - 'busy'          : request suspender/reactivar en vuelo.
 *  - 'suspend-error' : la suspensión falló; el formulario de motivo sigue
 *                      visible para reintentar sin reescribir el motivo.
 *  - 'error'         : una reactivación falló; message visible en la fila.
 */
type RowState =
  | { kind: 'idle' }
  | { kind: 'prompting' }
  | { kind: 'busy' }
  | { kind: 'suspend-error'; message: string }
  | { kind: 'error'; message: string };

const IDLE: RowState = { kind: 'idle' };

/**
 * Lista de tenants del SaaS. Permite suspender / reactivar cada tenant.
 * Requiere la cookie de sesión: un 401 redirige al login.
 */
export function Tenants() {
  const navigate = useNavigate();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  // Estado por fila. Clave = tenant id. Ausencia = idle.
  const [rows, setRows] = useState<Record<string, RowState>>({});
  // Motivo de suspensión en edición, por tenant id.
  const [reasons, setReasons] = useState<Record<string, string>>({});

  /**
   * Carga (o recarga) la lista. `silent` evita el skeleton cuando ya hay
   * datos en pantalla — así un refresco tras suspender/reactivar no parpadea.
   */
  const load = useCallback(
    async (silent = false) => {
      if (!silent) setState({ kind: 'loading' });
      try {
        const res = await api.get<TenantsResponse>('/api/super-admin/tenants');
        // Estado vacío vs error: una respuesta 200 con lista vacía es "vacío",
        // nunca un error. Solo entramos a 'error' por fallo de red o status no-2xx.
        setState({ kind: 'ready', tenants: res.items ?? [] });
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          navigate('/login', { replace: true });
          return;
        }
        if (err instanceof NetworkError) {
          setState({
            kind: 'error',
            message: 'No se pudo conectar con el servidor.',
          });
          return;
        }
        const message =
          err instanceof ApiError ? err.message : 'Ocurrió un error inesperado.';
        setState({ kind: 'error', message });
      }
    },
    [navigate],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const setRow = useCallback((id: string, next: RowState) => {
    setRows((prev) => ({ ...prev, [id]: next }));
  }, []);

  /** Traduce un error de acción a un mensaje legible; 401 redirige al login. */
  const actionErrorMessage = useCallback(
    (err: unknown): string | null => {
      if (err instanceof ApiError && err.status === 401) {
        navigate('/login', { replace: true });
        return null;
      }
      if (err instanceof NetworkError) {
        return 'No se pudo conectar con el servidor.';
      }
      if (err instanceof ApiError) return err.message;
      return 'Ocurrió un error inesperado.';
    },
    [navigate],
  );

  const suspend = useCallback(
    async (tenant: Tenant) => {
      const reason = (reasons[tenant.id] ?? '').trim();
      if (reason === '') {
        setRow(tenant.id, {
          kind: 'suspend-error',
          message: 'Indica el motivo de la suspensión.',
        });
        return;
      }
      setRow(tenant.id, { kind: 'busy' });
      try {
        await api.post(`/api/super-admin/tenants/${tenant.id}/suspend`, {
          reason,
        });
        setReasons((prev) => {
          const next = { ...prev };
          delete next[tenant.id];
          return next;
        });
        setRow(tenant.id, IDLE);
        await load(true);
      } catch (err) {
        const message = actionErrorMessage(err);
        // 'suspend-error' mantiene visible el formulario de motivo para reintentar.
        if (message) setRow(tenant.id, { kind: 'suspend-error', message });
      }
    },
    [reasons, setRow, load, actionErrorMessage],
  );

  const reactivate = useCallback(
    async (tenant: Tenant) => {
      setRow(tenant.id, { kind: 'busy' });
      try {
        await api.post(`/api/super-admin/tenants/${tenant.id}/reactivate`);
        setRow(tenant.id, IDLE);
        await load(true);
      } catch (err) {
        const message = actionErrorMessage(err);
        if (message) setRow(tenant.id, { kind: 'error', message });
      }
    },
    [setRow, load, actionErrorMessage],
  );

  return (
    <>
      <header className="app-header">
        <div className="brand">
          <span className="brand-title">Inventario</span>
          <span className="brand-tag">Super Admin</span>
        </div>
      </header>

      <main className="app-main">
        <div className="row-between">
          <div>
            <h1 className="page-title">Tenants</h1>
            <p className="page-subtitle">
              Comercios registrados en la plataforma.
            </p>
          </div>
          {state.kind === 'ready' && (
            <span className="text-muted" style={{ fontSize: 13 }}>
              {state.tenants.length}{' '}
              {state.tenants.length === 1 ? 'tenant' : 'tenants'}
            </span>
          )}
        </div>

        <div className="mt-16">
          {state.kind === 'loading' && <TableSkeleton columns={6} />}

          {state.kind === 'error' && (
            <StateBlock
              title="No se pudieron cargar los tenants"
              description={state.message}
              action={{ label: 'Reintentar', onClick: () => void load() }}
            />
          )}

          {state.kind === 'ready' && state.tenants.length === 0 && (
            <StateBlock
              title="Aún no hay tenants"
              description="Cuando se registre un comercio aparecerá en esta lista."
            />
          )}

          {state.kind === 'ready' && state.tenants.length > 0 && (
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Plan</th>
                    <th>Creado</th>
                    <th>Usuarios</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {state.tenants.map((t) => {
                    const row = rows[t.id] ?? IDLE;
                    const busy = row.kind === 'busy';
                    return (
                      <tr
                        key={t.id}
                        className={t.suspended ? 'is-suspended' : undefined}
                      >
                        <td className="cell-name">
                          {/* Solo el nombre navega — la celda de acciones
                              tiene input + botones que NO deben disparar
                              navegación. */}
                          <Link to={`/tenants/${t.id}`} className="tenant-link">
                            {t.name}
                          </Link>
                        </td>
                        <td style={{ textTransform: 'capitalize' }}>{t.plan}</td>
                        <td>{formatDate(t.createdAt)}</td>
                        <td className="cell-num">{t.userCount}</td>
                        <td>
                          {/* Suspendido domina sobre conectado/pendiente. */}
                          {t.suspended ? (
                            <StatusBadge status="Suspendido" />
                          ) : (
                            <StatusBadge
                              status={t.connected ? 'conectado' : 'pendiente'}
                            />
                          )}
                        </td>
                        <td className="cell-actions">
                          {t.suspended ? (
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              disabled={busy}
                              onClick={() => void reactivate(t)}
                            >
                              {busy ? 'Reactivando…' : 'Reactivar'}
                            </button>
                          ) : row.kind === 'prompting' ||
                            row.kind === 'suspend-error' ||
                            busy ? (
                            <div className="suspend-form">
                              <input
                                type="text"
                                className="input"
                                placeholder="Motivo de la suspensión"
                                autoFocus
                                disabled={busy}
                                value={reasons[t.id] ?? ''}
                                onChange={(e) =>
                                  setReasons((prev) => ({
                                    ...prev,
                                    [t.id]: e.target.value,
                                  }))
                                }
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && !busy) {
                                    void suspend(t);
                                  }
                                }}
                              />
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm btn-danger-text"
                                disabled={busy}
                                onClick={() => void suspend(t)}
                              >
                                {busy ? 'Suspendiendo…' : 'Confirmar'}
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                disabled={busy}
                                onClick={() => {
                                  setRow(t.id, IDLE);
                                  setReasons((prev) => {
                                    const next = { ...prev };
                                    delete next[t.id];
                                    return next;
                                  });
                                }}
                              >
                                Cancelar
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm btn-danger-text"
                              onClick={() => setRow(t.id, { kind: 'prompting' })}
                            >
                              Suspender
                            </button>
                          )}
                          {(row.kind === 'error' ||
                            row.kind === 'suspend-error') && (
                            <div className="cell-error" role="alert">
                              {row.message}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </>
  );
}
